"use client"
import React from "react"
import { useEffect, useState } from "react"
import { usePostVote } from "../hooks/DataFetching/usePostVote"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faThumbsUp, faThumbsDown, faCircleQuestion } from "@fortawesome/free-solid-svg-icons"
import { Form, Button, Alert } from "react-bootstrap"
import { connect } from "react-redux"

export const PostVote = ({ campaignId, campaignStatus = "active", isDarkMode, onVoteSaved }) => {
  const { message, isSubmitVisible, currentVote, isLoadingVote, postVote, setIsSubmitVisible, setMessage } = usePostVote(campaignId)
  const [approvalStatus, setApprovalStatus] = useState("")
  const [voteReason, setVoteReason] = useState("")
  const [hoveredOption, setHoveredOption] = useState(null)
  const createdOn = new Date().toISOString()
  const isVotingOpen = String(campaignStatus).toLowerCase() === "active"

  useEffect(() => {
    if (!currentVote) return
    setApprovalStatus(currentVote.vote1 === false ? "Disapprove" : "Approve")
    setVoteReason(currentVote.voteReason || "")
  }, [currentVote])

  const handleModalSubmit = async () => {
    const savedVote = await postVote(campaignId, approvalStatus, voteReason, createdOn)
    if (savedVote && onVoteSaved) onVoteSaved(savedVote)
  }

  const handleAcknowledge = () => {
    setIsSubmitVisible(true)
    setMessage("")
  }

  const isSubmitDisabled = !approvalStatus || !isVotingOpen || isLoadingVote

  // Container styles
  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
  }

  const topSectionStyle = {
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
  }

  // Vote options section styles
  const voteOptionsContainerStyle = {
    flex: "2",
  }

  const voteOptionsStyle = {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
  }

  // Get vote option style with hover effects
  const getVoteOptionStyle = (option) => {
    const isHovered = hoveredOption === option

    return {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "15px",
      borderRadius: "8px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      width: "140px",
      border: "1px solid #dee2e6",
      backgroundColor: isDarkMode ? "#343a40" : "#f8f9fa",
      // Add hover effects
      transform: isHovered ? "translateY(-3px)" : "translateY(0)",
      boxShadow: isHovered ? "0 4px 8px rgba(0, 0, 0, 0.15)" : "none",
    }
  }

  // Vote preview section styles
  const votePreviewStyle = {
    flex: "1",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  }

  const votePreviewContainerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderRadius: "8px",
    backgroundColor: isDarkMode ? "#343a40" : "#f8f9fa",
    border: "1px solid #dee2e6",
    width: "100%",
  }

  const votePreviewLabelStyle = {
    fontSize: "0.9rem",
    marginBottom: "15px",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: 600,
    color: isDarkMode ? "#adb5bd" : "#6c757d",
  }

  // Submit button style
  const submitButtonStyle = {
    marginTop: "15px",
    width: "100%",
  }

  // Text input section styles
  const textInputSectionStyle = {
    width: "100%",
  }

  const textInputLabelStyle = {
    textAlign: "center",
    width: "100%",
    marginBottom: "10px",
    fontWeight: "bold",
    color: isDarkMode ? "#efece0" : "inherit",
  }

  return (
    <div style={containerStyle}>
      <div style={topSectionStyle}>
        {/* Vote options (left side) */}
        <div style={voteOptionsContainerStyle}>
          <div style={voteOptionsStyle}>
            <div
              style={getVoteOptionStyle("Approve")}
              onClick={() => isVotingOpen && setApprovalStatus("Approve")}
              onMouseEnter={() => setHoveredOption("Approve")}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <div style={{ marginBottom: "10px" }}>
                <FontAwesomeIcon icon={faThumbsUp} className="text-success" size="2x" />
              </div>
              <div style={{ fontWeight: 500, fontSize: "1.1rem" }}>Support</div>
            </div>

            <div
              style={getVoteOptionStyle("Disapprove")}
              onClick={() => isVotingOpen && setApprovalStatus("Disapprove")}
              onMouseEnter={() => setHoveredOption("Disapprove")}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <div style={{ marginBottom: "10px" }}>
                <FontAwesomeIcon icon={faThumbsDown} className="text-danger" size="2x" />
              </div>
              <div style={{ fontWeight: 500, fontSize: "1.1rem" }}>Oppose</div>
            </div>
          </div>
        </div>

        {/* Your Vote preview (right side) */}
        <div style={votePreviewStyle}>
          <div style={votePreviewContainerStyle}>
            <div style={votePreviewLabelStyle}>Your Vote</div>
            <div style={{ marginBottom: "10px" }}>
              {approvalStatus === "Approve" && (
                <FontAwesomeIcon icon={faThumbsUp} className="text-success" style={{ color: "#28a745" }} size="3x" />
              )}
              {approvalStatus === "Disapprove" && (
                <FontAwesomeIcon icon={faThumbsDown} className="text-danger" style={{ color: "#dc3545" }} size="3x" />
              )}
              {!["Approve", "Disapprove"].includes(approvalStatus) && (
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  className={isDarkMode ? "text-light" : "text-secondary"}
                  size="3x"
                />
              )}
            </div>
            <div style={{ fontWeight: 500 }}>
              {approvalStatus === "Approve" && <span className="text-success">Supporting</span>}
              {approvalStatus === "Disapprove" && <span className="text-danger">Opposing</span>}
              {!["Approve", "Disapprove"].includes(approvalStatus) && (
                <span className={isDarkMode ? "text-light" : "text-secondary"}>Undecided</span>
              )}
            </div>

            {/* Submit button under Your Vote */}
            {isSubmitVisible && (
              <Button
                variant="primary"
                onClick={handleModalSubmit}
                disabled={isSubmitDisabled}
                style={submitButtonStyle}
              >
                {currentVote ? "Update Vote" : "Submit Vote"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Text input section (full width) */}
      <div style={textInputSectionStyle}>
        <Form.Group>
          <Form.Label style={textInputLabelStyle}>Share your thoughts (optional)</Form.Label>
          <Form.Control
            as="textarea"
            value={voteReason}
            onChange={(e) => setVoteReason(e.target.value)}
            placeholder="Why do you support or oppose this idea?"
            className={isDarkMode ? "bg-dark text-light border-secondary" : "bg-light"}
            style={{ resize: "none", height: "80px", width: "100%" }}
          />
        </Form.Group>
      </div>

      {!isVotingOpen && (
        <Alert variant="info" className="mt-3">
          Voting is closed because this idea has already moved forward or reached a final outcome.
        </Alert>
      )}

      {/* Alert message */}
      {message && (
        <Alert variant="info" className="mt-3">
          <p className="mb-1">{message}</p>
          {!isSubmitVisible && (
            <div className="d-flex justify-content-end">
              <Button variant="outline-secondary" size="sm" onClick={handleAcknowledge} className="mt-2">
                Acknowledge
              </Button>
            </div>
          )}
        </Alert>
      )}
    </div>
  )
}

// Map Redux state to component props
const mapStateToProps = (state) => ({
  isDarkMode: state.theme.isDarkMode,
})

// Connect component to Redux
export default connect(mapStateToProps)(PostVote)
